export const SITE = {
  name: "factory.online",
  tagline: "Production that never goes off track",
  email: "office@factory.online",
  appUrl: "https://cloud.factory.online/",
  supportUrl: "https://support.factory.online/",
  developerUrl: "https://developer.factory.online/",
  beedigitUrl: "https://www.beedigit.com/",
};

export type SocialLink = {
  id: string;
  label: string;
  href: string;
  ukHref?: string;
};

export const SOCIAL_LINKS: SocialLink[] = [
  { id: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/company/factory-online-service" },
  { id: "youtube", label: "YouTube", href: "https://www.youtube.com/@factoryonlineservice" },
  {
    id: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/factoryonlineservice/",
    ukHref: "https://www.instagram.com/factory.online_ua/",
  },
  { id: "facebook", label: "Facebook", href: "https://www.facebook.com/factoryonlineservice" },
];

export type NavItem = { label: string; to: string };
export type NavGroup = { label: string; items: NavItem[] };

export const FEATURE_NAV: NavItem[] = [
  { label: "Sales", to: "/sales" },
  { label: "Production", to: "/production" },
  { label: "Supply Chain", to: "/purchases" },
  { label: "Inventory Management", to: "/stocks" },
  { label: "Planning", to: "/planning" },
  { label: "Scheduling", to: "/scheduling" },
  { label: "Costing", to: "/cost" },
  { label: "Master and Product Data", to: "/technical-data" },
];

export const INDUSTRY_NAV: NavItem[] = [
  { label: "Cable & Wire Manufacturing", to: "/industries/cable-wire-manufacturing" },
  { label: "Fiber Optic Cables", to: "/industries/fiber-optic-cables" },
  { label: "Assembly & Miltech", to: "/industries/metals-mechanical-engineering" },
  { label: "Aluminum & Extrusion", to: "/industries/aluminum-extrusion" },
  { label: "Food & Feed Production", to: "/industries/food-feed-production" },
  { label: "Chemical & Process Manufacturing", to: "/industries/chemical-process-manufacturing" },
  { label: "Jobshop & Custom Production", to: "/industries/jobshop-custom-production" },
  { label: "All industries", to: "/industries" },
];

export const RESOURCE_NAV: NavItem[] = [
  { label: "Blog", to: "/blog" },
  { label: "ROI calculator", to: "/roi-calculator" },
  { label: "Onboarding programs", to: "/onboarding" },
  { label: "Integrations", to: "/integration" },
  { label: "Partnership program", to: "/affiliate-program" },
];

export const ABOUT_NAV: NavItem[] = [
  { label: "About us", to: "/about" },
  { label: "Contacts", to: "/contacts" },
  { label: "News", to: "/news" },
  { label: "Reusing content", to: "/reusing-content" },
];

export const HERO = {
  title: "Cognitive Manufacturing",
  lead: "factory.online is a cloud manufacturing platform where AI agents assist planning, costing and shop-floor decisions — sales, production and inventory in one real-time system.",
  points: [
    "AI agents that recommend — your team stays in control",
    "No chaos, no Excel traps",
    "Works instantly after connection, no complex setup",
  ],
  primaryCta: { label: "Book a demo", to: "/registration-demo" },
  secondaryCta: { label: "Talk to a consultant", to: "/contacts" },
};

export const CHAOS = {
  title: "Why you need factory.online",
  lead: "Clear processes, full control, and profit without unnecessary hassle.",
  without: {
    title: "Without factory.online",
    items: [
      "Data in Excel, accessible only to a few people",
      "Manual calculations lead to errors and losses",
      "Job orders get lost, changes get mixed up",
      "Inventory runs out at the critical moment, or dead money sits in the warehouse",
      "Planning takes days instead of minutes",
      "Customers wait while managers can't say when the order will be ready",
    ],
  },
  with: {
    title: "With factory.online",
    items: [
      "All orders, warehouses and manufacturing in one system",
      "Automatic planning of material and resource demand",
      "Transparent management of manufacturing schedules",
      "Forecasting risks of shortages and disruptions",
      "Integration with ERP, CRM and accounting systems",
      "Real-time dashboards for managers",
      "Complete transparency and rapid scalability",
    ],
  },
};

export type Capability = {
  slug: string;
  title: string;
  summary: string;
  items: string[];
};

export const CAPABILITIES: Capability[] = [
  {
    slug: "sales",
    title: "Sales",
    summary:
      "Quote, confirm, dispatch and invoice from one order book that production can actually see.",
    items: [
      "Sales Orders",
      "Quotes",
      "Invoices",
      "Products Dispatch",
      "Customer Returns",
      "Customer Management",
    ],
  },
  {
    slug: "production",
    title: "Production",
    summary:
      "Manufacturing orders, work orders and WIP tracking from release to finished-goods receipt.",
    items: [
      "Manufacturing Orders (MOs)",
      "Material and Resource Requirements Planning (MRPII)",
      "Production Tasks / Work Orders",
      "Work in Progress (WIP) tracking",
      "Finished Goods Receipt to Warehouse",
    ],
  },
  {
    slug: "purchases",
    title: "Supply Chain",
    summary:
      "Requisitions, purchase orders and supplier performance driven by real demand, not guesswork.",
    items: [
      "Purchase Orders",
      "Goods Receipt",
      "Purchase Requisitions",
      "Supplier Management",
      "Materials Demand Forecasting",
    ],
  },
  {
    slug: "stocks",
    title: "Inventory Management",
    summary: "Batch-level stock accuracy across every location, with transfers and stock-taking.",
    items: ["Stocks", "Batches", "Stock-taking", "Transfer Requests", "Stock Transfers"],
  },
  {
    slug: "planning",
    title: "Planning",
    summary: "Rough-cut capacity planning that answers whether the plan is even possible.",
    items: [
      "Rough-Cut Capacity Planning (RCCP)",
      "Material requirements planning",
      "Demand and supply balancing",
    ],
  },
  {
    slug: "scheduling",
    title: "Scheduling",
    summary: "Advanced planning and scheduling down to the work centre and the shift.",
    items: [
      "Scheduling (APS)",
      "Work calendars, shifts and holidays",
      "Alternative work centres",
      "Finite capacity sequencing",
    ],
  },
  {
    slug: "cost",
    title: "Product Costing",
    summary: "Know the real cost of every order — standard, actual, and the gap between them.",
    items: ["Standard Costing", "Actual Costing", "Standard vs Actual comparison"],
  },
  {
    slug: "technical-data",
    title: "Master and Product Data",
    summary: "One clean backbone of items, BOMs and routings that every module reads from.",
    items: [
      "Items",
      "Item classes and characteristics",
      "Bill of Materials",
      "Routings",
      "Packaging types and BOMs",
      "Process and Dispatch Containers",
      "Locations",
      "Work Centers",
    ],
  },
];

export const RESULTS = [
  { value: "-20%", label: "Operating costs" },
  { value: "-23%", label: "Inventory" },
  { value: "95+%", label: "Data accuracy" },
  { value: "97%", label: "Production on-time" },
  { value: "days → minutes", label: "Order processing time" },
  { value: "+15%", label: "Equipment utilization" },
];

export const TESTIMONIALS = [
  {
    quote:
      "Process automation and convenient order tracking improved shop floor efficiency. Production became faster and more stable, without sacrificing quality.",
    highlight: "+20% increase in production throughput",
    name: "Liam B.",
    role: "Planner",
  },
  {
    quote:
      "We finally see material demand before it becomes a shortage. Purchasing plans against the real schedule instead of a spreadsheet.",
    highlight: "Shortages caught weeks earlier",
    name: "Marta K.",
    role: "Supply Chain Manager",
  },
  {
    quote:
      "Managers stopped asking where an order is. The dashboard answers it, and the answer is the same for everyone.",
    highlight: "One source of truth across the plant",
    name: "Andrii S.",
    role: "Operations Director",
  },
];

export const INDUSTRIES = [
  "Machinery and metalworking",
  "Cable and electrical equipment",
  "Woodworking and furniture",
  "Food industry",
  "Pharmaceuticals and cosmetics",
  "Construction materials",
  "Textiles and light industry",
  "Agriculture",
];

export const SECURITY = [
  {
    title: "Microsoft Azure",
    body: "Data hosted on secure Microsoft Azure servers that meet global security standards.",
  },
  { title: "256-bit encryption", body: "The same level of encryption used by banks." },
  { title: "GDPR-compliant", body: "Data processed under EU privacy regulations." },
  {
    title: "ISO certifications",
    body: "Proving reliability and top-level system security.",
  },
];

export const ABOUT_POINTS = [
  "13+ years of experience in manufacturing consulting",
  "English and Arabic-speaking support team",
  "Modern, accessible cloud services for manufacturers that just work",
  "Transparency, speed and real-time control",
  "We help businesses grow faster and make fewer mistakes",
];

export const ECOSYSTEM = {
  title: "Unify business processes and the shop floor in one digital ecosystem",
  lead: "Together with BEEDIGIT you get full production control.",
  items: [
    "Real-time equipment monitoring",
    "Performance, energy use and efficiency loss tracking",
    "Plug & play connection of any sensors or machines",
    "One digital loop: from planning to machine-level execution",
    "Integration with no extra costs",
  ],
};

export const FAQS = [
  {
    q: "Is factory.online suitable for small businesses?",
    a: "Yes. We recommend the right plan for your current operation and you can scale as you grow.",
  },
  {
    q: "What standards does the system support?",
    a: "factory.online implements the MRPII, MRP, APS, SCP and WMS standards and concepts.",
  },
  {
    q: "How much time does implementation take?",
    a: "On average 120 days, delivered phase by phase.",
  },
  {
    q: "Can I manage work calendars and shifts?",
    a: "Yes. You can configure work calendars, shifts and holidays, and adapt schedules to real production conditions.",
  },
  {
    q: "Does it support alternative work centers and materials?",
    a: "Yes. Alternative work centres optimise equipment load, and substitute materials are considered in planning to reduce supply risk.",
  },
  {
    q: "Does factory.online integrate with other systems?",
    a: "Yes, via API with accounting systems, ERP systems (SAP, Oracle, Dynamics, BAS) and CRM platforms.",
  },
  {
    q: "Can the system connect to equipment?",
    a: "Yes. Through integration with BEEDIGIT you can connect any machine or line and track performance, energy consumption and parameters in real time.",
  },
  {
    q: "Which production strategies are supported?",
    a: "Make to Stock (MTS), Make to Order (MTO) and Assemble to Order (ATO).",
  },
  {
    q: "Can it calculate resource requirements?",
    a: "Yes. The system calculates equipment and labour needs, taking worker skill levels into account.",
  },
  {
    q: "Do I need my own server?",
    a: "No. factory.online is fully cloud-based and runs on Microsoft Azure infrastructure.",
  },
];
